/* eslint-disable fp/no-mutation */
import {
  Alert,
  Box,
  Button,
  Card,
  IconButton,
  Paper,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  AddIcon,
  QuizIcon,
  RemoveIcon,
  SaveIcon,
  TextContentIcon,
  TitleScreenIcon,
  VideoIcon,
} from 'src/v3/core/icons'

import { v4 as uuidv4 } from 'uuid'
import { ReactElement, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd'
import {
  fetchClasses,
  selectActiveClass,
  selectClassByID,
  selectClassesStatus,
  fetchTitleScreens,
  selectTitleScreenByClassID,
  selectTitleScreensStatus,
  fetchVideos,
  selectVideosByClassID,
  selectVideosStatus,
  fetchTextContent,
  selectTextContentByClassID,
  selectTextContentStatus,
  fetchQuizzes,
  selectQuizByClassID,
  selectQuizzesStatus,
  fetchStoryboardSteps,
  selectStoryboardStepsByClassID,
  selectStoryboardStepsStatus,
} from 'src/v3/create/create.slice'
import axios from 'src/utilities/http-client'
import { StreamStep } from 'src/v3/core/types'
import Loading from 'src/v3/components/loading'

let moveItem = (list: any[], oldIndex: number, newIndex: number) => {
  let without = list.filter((x, i) => i !== oldIndex)
  return [
    ...without.slice(0, newIndex),
    list[oldIndex],
    ...without.slice(newIndex),
  ]
}

export default (): ReactElement => {
  let navigate = useNavigate()
  let dispatch = useAppDispatch()

  let classesStatus = useAppSelector(selectClassesStatus)
  let activeClass = useAppSelector(selectActiveClass)
  let classData = useAppSelector(selectClassByID(activeClass))

  let titleScreen = useAppSelector(selectTitleScreenByClassID(activeClass))
  let titleScreensStatus = useAppSelector(selectTitleScreensStatus)
  let videos = useAppSelector(selectVideosByClassID(activeClass)) || []
  let videosStatus = useAppSelector(selectVideosStatus)
  let textContent =
    useAppSelector(selectTextContentByClassID(activeClass)) || []
  let textContentStatus = useAppSelector(selectTextContentStatus)
  let quiz = useAppSelector(selectQuizByClassID(activeClass))
  let quizStatus = useAppSelector(selectQuizzesStatus)

  let existingStoryboard = useAppSelector(
    selectStoryboardStepsByClassID(activeClass),
  )
  let storyboardStatus = useAppSelector(selectStoryboardStepsStatus)

  let newSteps: Partial<StreamStep>[] = [
    ...textContent
      .filter(
        (tc) => !existingStoryboard.some((ss) => ss.textContentID === tc.id),
      )
      .map((tc) => ({ id: uuidv4(), textContentID: tc.id })),
    ...videos
      .filter((v) => !existingStoryboard.some((ss) => ss.videoID === v.id))
      .map((v) => ({ id: uuidv4(), videoID: v.id })),
  ]

  let [storyboard, setStoryboard] =
    useState<Partial<StreamStep>[]>(existingStoryboard)
  let [unusedSteps, setUnusedSteps] = useState<Partial<StreamStep>[]>(newSteps)
  let [saving, setSaving] = useState<boolean>(false)
  let [errorMessage, setErrorMessage] = useState<string>('')

  useEffect(() => {
    if (classesStatus === 'uninitialized') {
      dispatch(fetchClasses())
    }
  }, [classesStatus])

  useEffect(() => {
    if (storyboardStatus === 'uninitialized') {
      dispatch(fetchStoryboardSteps())
    }
  }, [storyboardStatus])

  useEffect(() => {
    if (titleScreensStatus === 'uninitialized') {
      dispatch(fetchTitleScreens(activeClass))
    }
  }, [titleScreensStatus])

  useEffect(() => {
    if (videosStatus === 'uninitialized') {
      dispatch(fetchVideos())
    }
  }, [videosStatus])

  useEffect(() => {
    if (textContentStatus === 'uninitialized') {
      dispatch(fetchTextContent(activeClass))
    }
  }, [textContentStatus])

  useEffect(() => {
    if (quizStatus === 'uninitialized') {
      dispatch(fetchQuizzes(activeClass))
    }
  }, [quizStatus])

  useEffect(() => {
    if (
      [
        storyboardStatus,
        titleScreensStatus,
        textContentStatus,
        videosStatus,
        quizStatus,
      ].every((s) => s === 'fulfilled')
    ) {
      setStoryboard(existingStoryboard)
      setUnusedSteps(newSteps)
    }
  }, [
    storyboardStatus,
    titleScreensStatus,
    textContentStatus,
    videosStatus,
    quizStatus,
  ])

  useEffect(() => {
    if (activeClass === 'unselected') {
      navigate('/create')
    }
  }, [activeClass])

  return (
    <>
      <Box component="main" sx={{ p: 3 }}>
        <Typography
          variant="h4"
          className="my-4 d-inline"
          style={{
            fontFamily: 'smileAndWave',
          }}
        >
          Storyboard
        </Typography>
        <Typography
          className="d-inline ml-3"
          sx={{
            marginBottom: '16px',
          }}
        >
          Storyboard for {classData?.name}
        </Typography>
        {!existingStoryboard?.length ? (
          <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
            <Typography sx={{ mb: 0 }}>
              No storyboard exists for this class.
            </Typography>
            {!titleScreen && (
              <Box>
                <Typography sx={{ mt: 1 }}>
                  Before a storyboard can be made, {classData?.name} needs a
                  title screen.
                </Typography>
                <Button
                  sx={{ mt: '4px' }}
                  variant="outlined"
                  size="medium"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/create/title-screen')}
                >
                  Create Title Screen
                </Button>
              </Box>
            )}
            {!quiz && (
              <Box>
                <Typography sx={{ mt: 1 }}>
                  Before a storyboard can be made, {classData?.name} needs a
                  quiz.
                </Typography>
                <Button
                  sx={{ mt: '4px' }}
                  variant="outlined"
                  size="medium"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/create/quiz')}
                >
                  Create Quiz
                </Button>
              </Box>
            )}
            {titleScreen && quiz && (
              <Button
                sx={{ mt: 1 }}
                variant="contained"
                size="medium"
                startIcon={<AddIcon />}
                disabled={activeClass === 'unselected'}
                onClick={() => {
                  setSaving(true)
                  axios
                    .post('/api/v2/create/stream', {
                      classID: activeClass,
                      steps: [
                        { titleScreen: { titleScreenID: titleScreen?.id } },
                        ...(videos || []).map((v) => ({
                          video: { videoID: v.id },
                        })),
                        ...(textContent || []).map((tc) => ({
                          textContent: { textContentID: tc.id },
                        })),
                        { quiz: { quizID: quiz?.id } },
                      ],
                    })
                    .then(() => dispatch(fetchStoryboardSteps()))
                    .then(() => setSaving(false))
                    .catch((err) => {
                      setErrorMessage(
                        err?.message || 'Error Creating Storyboard',
                      )
                      dispatch(fetchStoryboardSteps())
                      setSaving(false)
                    })
                }}
              >
                Create Storyboard
              </Button>
            )}
          </Paper>
        ) : (
          <>
            <Typography variant="subtitle1" sx={{ my: 1 }}>
              The storyboard is what determines the order in which your class
              content is presented. Content can be rearranged and added or
              removed from the class flow as needed.
            </Typography>
            <Card variant="outlined" sx={{ p: 2, pt: 2.25, mb: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Set the order of class content by dragging and dropping video
                and text content in the desired order.
              </Typography>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                To remove content from the storyboard, click the minus sign to
                the right. Doing so will only remove the content from the
                storyboard; it will not delete it entirely. Removed content can
                be added again below.
              </Typography>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{
                  marginBottom: '16px',
                }}
              >
                *Note that title screen and quiz cannot be reordered: they are
                always first and last, respectively.
              </Typography>
              <DragDropContext
                onDragEnd={(result) => {
                  if (
                    !result.destination ||
                    result.destination.index === 0 ||
                    // eslint-disable-next-line no-unsafe-optional-chaining
                    result.destination.index === storyboard?.length - 1
                  ) {
                    return
                  }
                  setStoryboard(
                    moveItem(
                      storyboard,
                      result.source.index,
                      result.destination?.index,
                    ),
                  )
                }}
              >
                <Droppable droppableId="storyboard">
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {storyboard?.map((step, i) => {
                        let name: string | undefined =
                          `${classData?.name} Title Screen`
                        let icon = <TitleScreenIcon />
                        if (step.videoID) {
                          name = videos.find((v) => v.id === step.videoID)?.name
                          icon = <VideoIcon />
                        } else if (step.textContentID) {
                          name = textContent.find(
                            (tc) => tc.id === step.textContentID,
                          )?.name
                          icon = <TextContentIcon />
                        } else if (step.quizID) {
                          name = `${classData?.name} Quiz`
                          icon = <QuizIcon />
                        }
                        let draggable = i !== 0 && !name?.includes('Quiz')

                        return (
                          <Draggable
                            key={step.id}
                            draggableId={step.id || i.toString()}
                            index={i}
                            isDragDisabled={!draggable}
                          >
                            {(prov) => (
                              <Card
                                sx={{ p: 2, mt: i ? 2 : 0, display: 'flex' }}
                                ref={prov.innerRef}
                                {...prov.draggableProps}
                                {...prov.dragHandleProps}
                              >
                                {icon}
                                <Typography sx={{ flexGrow: 1, ml: 2 }}>
                                  {name}
                                </Typography>
                                {draggable && (
                                  <Tooltip title="Remove from Storyboard">
                                    <IconButton
                                      color="error"
                                      sx={{ my: -1 }}
                                      disabled={activeClass === 'unselected'}
                                      onClick={() => {
                                        setStoryboard(
                                          storyboard.filter(
                                            (s) => s.id !== step.id,
                                          ),
                                        )
                                        setUnusedSteps([...unusedSteps, step])
                                      }}
                                    >
                                      <RemoveIcon />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {/* {draggable && <DragIcon />} */}
                              </Card>
                            )}
                          </Draggable>
                        )
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </Card>
            <Card variant="outlined" sx={{ p: 2, pt: 2.25, mb: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                This is the class content you have created, but have not yet
                added to the storyboard. To add a video or text content to the
                storyboard, click the plus sign on the right.
              </Typography>
              {!unusedSteps.length && (
                <Typography variant="subtitle2" color="text.secondary">
                  There is no unused text content or videos.
                </Typography>
              )}
              {unusedSteps?.map((step, i) => (
                <Card
                  key={step.id}
                  sx={{ p: 2, mt: i ? 2 : 0, display: 'flex' }}
                >
                  {step.videoID ? <VideoIcon /> : <TextContentIcon />}
                  <Typography sx={{ flexGrow: 1, ml: 2 }}>
                    {step.videoID
                      ? videos.find((v) => v.id === step.videoID)?.name
                      : textContent.find((tc) => tc.id === step.textContentID)
                          ?.name}
                  </Typography>
                  <Tooltip title="Add to Storyboard">
                    <IconButton
                      color="success"
                      sx={{ my: -1 }}
                      disabled={activeClass === 'unselected'}
                      onClick={() => {
                        setStoryboard([
                          ...storyboard.slice(0, storyboard.length - 1),
                          step,
                          storyboard[storyboard.length - 1],
                        ])
                        setUnusedSteps(
                          unusedSteps.filter((s) => s.id !== step.id),
                        )
                      }}
                    >
                      <AddIcon />
                    </IconButton>
                  </Tooltip>
                </Card>
              ))}
            </Card>
            <Button
              variant="outlined"
              size="large"
              startIcon={<SaveIcon />}
              disabled={activeClass === 'unselected'}
              onClick={() => {
                setSaving(true)
                axios
                  .post('/api/v2/create/stream', {
                    classID: activeClass,
                    steps: [
                      { titleScreen: { titleScreenID: titleScreen?.id } },
                      ...storyboard.slice(1, storyboard.length - 1).map((s) =>
                        s.videoID
                          ? { video: { videoID: s.videoID } }
                          : {
                              textContent: {
                                textContentID: s.textContentID,
                              },
                            },
                      ),
                      { quiz: { quizID: quiz?.id } },
                    ],
                  })
                  .then(() => dispatch(fetchStoryboardSteps()))
                  .then(() => setSaving(false))
                  .catch((err) => {
                    setErrorMessage(err?.message || 'Error Updating Storyboard')
                    dispatch(fetchStoryboardSteps())
                    setSaving(false)
                  })
              }}
            >
              Save Storyboard
            </Button>
          </>
        )}
      </Box>
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      <Loading
        entities={[
          classesStatus,
          quizStatus,
          storyboardStatus,
          textContentStatus,
          titleScreensStatus,
          videosStatus,
        ]}
        loading={saving}
      />
    </>
  )
}
